import { Box, Button, Icon, Text, Stack, SkeletonCircle, Input, IconButton, Spinner } from '@chakra-ui/react'
import { Avatar, AvatarGroup } from './ui/avatar'
import { FiEdit } from "react-icons/fi";
import { LuSearch, LuX } from "react-icons/lu";
import { ColorModeButton, useColorModeValue } from './ui/color-mode';
import { ThemeButton } from './ui/theme-button';
import { SettingsModal } from './settings';
import { useAccount } from './ui/account-context';
import { ChatAccordion } from './ui/chat-accordion';
import { ChatSession } from './chat-history';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from './ui/auth-context';
import { InputGroup } from './ui/input-group';
import { searchConversations as apiSearchConversations } from '@/api';
// Update SidebarProps interface
interface SidebarProps {
    setFontFamily: Function,
    fontFamily: string,
    onNewSession: () => void, // Handler from Block to start a new session
    // Add props related to chat history management
    chatSessions: () => ChatSession[]; // Array of chat session summaries
    onOldChatSelected: (sessionId: string) => void; // Handler when a history item is clicked
    onDeleteChat: (sessionId: string) => void; // Handler when delete is clicked for a session
    loadingSessions?: boolean; // New prop for loading state of chat history

    // Props for Chakra Box (position, maxW, etc.) - accept rest props
    [key: string]: any; // Allow any other props passed from parent (like maxW, position, etc.)
}


export const Sidebar = ({
    setFontFamily,
    fontFamily,
    onNewSession,
    chatSessions,
    onOldChatSelected,
    onDeleteChat,
    loadingSessions,
    ...rest
}: SidebarProps) => {

    const { accountType, setAccountType, currentSessionId } = useAccount();
    const { user, loading } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ChatSession[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [displayLimit, setDisplayLimit] = useState(20);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isActiveSearch = searchQuery.length >= 2;

    useEffect(() => {
        if (!isActiveSearch) {
            setSearchResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const results = await apiSearchConversations(searchQuery);
                setSearchResults(results || []);
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchQuery]);

    const allSessions = chatSessions();
    const visibleSessions: ChatSession[] = isActiveSearch
        ? searchResults
        : allSessions.slice(0, displayLimit);
    const hasMore = !isActiveSearch && allSessions.length > displayLimit;

    const handleNewChatClick = () => {
        onNewSession();
    }

    const handleClearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
    }

    const handleLoadMore = () => {
        setDisplayLimit(prev => prev + 20);
    }

    return (
        <Box p={2} {...rest} bgColor={useColorModeValue("#f9f9f9", "bg")} display={'flex'} flexDir={'column'} width={'100%'} height="100%">
            {/* New Chat Button - Fixed at top */}
            <Button
                onClick={handleNewChatClick} // Use the handler
                variant={'ghost'}
                p={1}
                gap={2}
                display={'flex'}
                justifyContent={'space-between'}
                alignItems={'center'}
                flexDir={'row'}
                height={'min-content'}
                width={'100%'}
                mb={4} // Add margin below button
            >
                <Box gap={2} display={'flex'} justifyContent={'flex-start'} alignItems={'center'} flexDir={'row'}>
                    <AvatarGroup >
                        <SkeletonCircle loading={loading} size={'10'}>
                            <Avatar fontFamily={fontFamily} name={user?.name} size={'md'} />
                        </SkeletonCircle>
                    </AvatarGroup>
                    <Text fontFamily={fontFamily}>New Chat</Text>
                </Box>
                <Icon boxSize={5} mr={2} aria-hidden="true">
                    <FiEdit />
                </Icon>
            </Button>

            {/* Search Input */}
            <Box px={2} mb={4}>
                <InputGroup
                    width="100%"
                    startElement={isSearching ? <Spinner size="xs" color="gray.400" /> : <LuSearch color="gray.400" />}
                    endElement={
                        searchQuery ? (
                            <IconButton
                                variant="ghost"
                                size="xs"
                                aria-label="Clear search"
                                onClick={handleClearSearch}
                                color="gray.400"
                                _hover={{ color: 'fg' }}
                            >
                                <LuX />
                            </IconButton>
                        ) : undefined
                    }
                >
                    <Input
                        placeholder="Search chats..."
                        aria-label="Search chats"
                        variant="subtle"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        fontFamily={fontFamily}
                        size="sm"
                    />
                </InputGroup>
            </Box>

            {/* Chat History List (ChatAccordion) - Flexible and scrollable area */}
            <Box flex="1" display="flex" flexDir="column" minH={0}>
                <ChatAccordion
                    sessions={visibleSessions}
                    onOldChatSelected={onOldChatSelected}
                    onDeleteChat={onDeleteChat}
                    fontFamily={fontFamily}
                    currentSessionId={currentSessionId}
                    loading={loadingSessions}
                    searchQuery={searchQuery}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                />
            </Box>

            {/* Bottom Sidebar (Settings, Theme, Color Mode) */}
            <Box borderTop="1px solid" borderTopColor={useColorModeValue("gray.200", "gray.700")} pt={4}>
                <Stack p={2} direction={'row'} justifyContent={'space-evenly'} alignItems={'center'} height={'min-content'} width={'100%'}>
                    <SettingsModal fontFamily={fontFamily} />
                    {accountType === 'admin' && (<ThemeButton setFontFamily={setFontFamily} fontFamily={fontFamily} />)}
                    <ColorModeButton />
                </Stack>
            </Box>
        </Box>
    )
}